// @ts-nocheck
/**
 * Admin Dashboard Component - Carbon Design System Compliant
 * Platform-wide administration interface integrated with CarbonUIShell
 * Fixed dual header issue and React prop type violations
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './AdminDashboard.module.scss';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tile,
  Stack,
  Button,
  InlineNotification,
  Loading,
  Tag,
  Heading,
  Grid,
  Column,
  Modal,
  FluidTextInput,
  Form,
  FormGroup,
  ProgressIndicator,
  ProgressStep,
} from '@carbon/react';
import {
  Restart,
  Add,
  DatabaseMessaging,
  Security,
  Settings,
  Api,
  Dashboard as DashboardIcon,
} from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import AdminNavigation from '../../components/admin/AdminNavigation';
import DashboardMetrics from '../../components/admin/DashboardMetrics';
import ContentManagementAdmin from '../../components/admin/ContentManagementAdmin';
import MultiTenantManagement from '../../components/admin/MultiTenantManagement';
import InstitutionalAnalytics from '../../components/admin/InstitutionalAnalytics';
import ABTestingDashboard from '../../components/admin/ABTestingDashboard';
import PlatformMonitoringDashboard from '../../components/admin/PlatformMonitoringDashboard';
import DataManagement from './DataManagement';
import { CacheStatus } from '../../components/cache/CacheStatus';
import StudentBulkCreation from '../school/StudentBulkCreation';
import CourseLibrary from '../../components/admin/CourseLibrary';
import EnterprisePrivacy from '../../components/admin/EnterprisePrivacy';
import DataRoutingDashboard from '../../components/admin/DataRoutingDashboard';
import RBACCacheManagement from '../../components/admin/RBACCacheManagement';

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface PlatformStats {
  total_users: number;
  total_students: number;
  total_parents: number;
  total_teachers: number;
  total_admins: number;
  total_families: number;
  active_sessions: number;
}

interface RecentUser {
  user_id: string;
  email: string;
  username: string;
  role: string;
  created_at: string;
}

interface SystemHealth {
  database: string;
  api: string;
  aiServices: string;
  storage: string;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealth>({
    database: 'checking',
    api: 'checking',
    aiServices: 'checking',
    storage: 'checking',
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [marketingMetrics, setMarketingMetrics] = useState({
    conversionRate: 10.9,
    activeTests: 3,
    cacheHitRate: 0, // Will be fetched from actual cache stats
    contentItems: 156,
  });
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);
  const [isSideNavOpen, setIsSideNavOpen] = useState(true);

  // Sync activeTab with URL path
  useEffect(() => {
    const path = location.pathname;
    if (path === '/admin/platform-monitoring') {
      setActiveTab('platform-monitoring');
    } else if (path === '/admin/data-management') {
      setActiveTab('data-management');
    } else if (path === '/admin/data-routing') {
      setActiveTab('data-routing');
    } else if (path === '/admin/users') {
      setActiveTab('users');
    } else if (path === '/admin/marketing') {
      setActiveTab('marketing');
    } else if (path === '/admin/marketing/analytics') {
      setActiveTab('analytics');
    } else if (path === '/admin/ab-testing') {
      setActiveTab('ab-testing');
    } else if (path === '/admin/content-management') {
      setActiveTab('marketing');
    } else if (path === '/admin/cache-management') {
      setActiveTab('cache-management');
    } else if (path === '/admin/compliance') {
      setActiveTab('security-settings');
    } else if (path === '/admin/storage') {
      setActiveTab('data-management');
    } else if (path === '/admin' || path === '/dashboard') {
      setActiveTab('overview');
    }
  }, [location.pathname]);

  // Fetch admin dashboard data
  useEffect(() => {
    fetchDashboardData();
    fetchCacheMetrics();

    // Refresh cache metrics every 30 seconds
    const interval = setInterval(fetchCacheMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchCacheMetrics = async () => {
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/cache/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        const stats = response.data.cache_stats || response.data;
        const hitRate = stats.hitRate || (stats.totalRequests > 0 ? (stats.hits / stats.totalRequests) * 100 : 0);

        setMarketingMetrics(prev => ({
          ...prev,
          cacheHitRate: parseFloat(hitRate.toFixed(1)),
        }));
      }
    } catch (error) {
      console.error('Failed to fetch cache metrics:', error);
      // Keep using default value on error
    }
  };

  const fetchDashboardData = async () => {
    // Epic17 FIX: Wrap entire function in try-catch-finally to ensure loading state is ALWAYS set to false
    try {
      setLoading(true);
      setError(null); // Clear previous errors
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

      if (!token) {
        // Epic17 CRITICAL FIX: Don't return early - let finally block run to clear loading state
        setError('No authentication token found. Please log in again.');
        throw new Error('No authentication token available');
      }

      const response = await axios.get(`${API_BASE_URL}/dashboard/admin`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 10000, // Epic17: Add 10 second timeout to prevent infinite loading
      });

      // Epic17 FIX: Safely access response data with defensive checks
      if (response && response.data && response.data.success) {
        const responseData = response.data.data || {};
        const platformStats = responseData.platformStats || {};
        const recentSignups = responseData.recentSignups || [];
        const health = responseData.systemHealth || {};

        // Epic17 FIX: Wrap state updates in try-catch to prevent errors from breaking flow
        try {
          setStats({
            total_users: platformStats.total_users || 0,
            total_students: platformStats.total_students || 0,
            total_parents: platformStats.total_parents || 0,
            total_teachers: platformStats.total_teachers || 0,
            total_admins: platformStats.total_admins || 0,
            total_families: platformStats.total_families || 0,
            active_sessions: platformStats.active_sessions || 0,
          });

          setRecentUsers(recentSignups);
          setSystemHealth({
            database: health.database || 'healthy',
            api: health.api || 'healthy',
            aiServices: health.aiServices || 'healthy',
            storage: health.storage || 'healthy',
          });
        } catch (stateError) {
          console.error('Error setting dashboard state:', stateError);
          setError('Failed to process dashboard data. Please refresh the page.');
        }
      } else {
        // Epic17: Handle API returning success=false or malformed response
        const errorMsg = response?.data?.error || 'Failed to load dashboard data';
        console.warn('Dashboard API returned non-success response:', response?.data);
        setError(errorMsg);
      }
    } catch (error: any) {
      console.error('Failed to fetch admin dashboard:', error);
      // Epic17: Set user-friendly error message
      if (error.response?.status === 403) {
        setError('Access denied. You may not have permission to view this dashboard.');
      } else if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setError('Request timed out. The server may be experiencing high load.');
      } else {
        setError('Failed to load dashboard data. Please try again.');
      }
    } finally {
      // Epic17 FIX: CRITICAL - Always set loading to false, even if there are errors
      // This ensures the loading screen is never stuck
      setLoading(false);
    }
  };

  const handleSeedTestUsers = async () => {
    try {
      setOperationInProgress(true);
      const response = await axios.post(`${API_BASE_URL}/test-users/seed-all`);
      if (response.data.success) {
        fetchDashboardData(); // Refresh data
      }
    } catch (error) {
      console.error('Failed to seed test users:', error);
    } finally {
      setOperationInProgress(false);
    }
  };

  const handleApplyRBACPermissions = async () => {
    try {
      setOperationInProgress(true);
      const response = await axios.post(`${API_BASE_URL}/rbac/apply-permissions`);
      if (response.data.success) {
        // Success feedback handled by UI
      }
    } catch (error) {
      console.error('Failed to apply RBAC permissions:', error);
    } finally {
      setOperationInProgress(false);
    }
  };

  const tableHeaders = [
    { key: 'user', header: 'User' },
    { key: 'role', header: 'Role' },
    { key: 'joined', header: 'Joined' },
  ];

  const tableRows = recentUsers.map(user => ({
    id: user.user_id,
    user: (
      <Stack gap={1}>
        <p className="cds--type-body-01">{user.username || user.email.split('@')[0]}</p>
        <p className="cds--type-helper-text-01" style={{ color: 'var(--cds-text-secondary)' }}>
          {user.email}
        </p>
      </Stack>
    ),
    role: <Tag type="outline">{user.role.toUpperCase()}</Tag>,
    joined: new Date(user.created_at).toLocaleDateString(),
  }));

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            {/* Epic17: Error State Display */}
            {error && (
              <InlineNotification
                title="Dashboard Load Error"
                subtitle={error}
                kind="error"
                lowContrast
                style={{ marginBottom: 'var(--cds-spacing-06)' }}
                actions={
                  <Button kind="ghost" size="sm" onClick={fetchDashboardData}>
                    Retry
                  </Button>
                }
              />
            )}

            {/* Alert */}
            <InlineNotification
              title="Administrator Access"
              subtitle="You have full platform administration privileges. Please use caution when making system-wide changes."
              kind="warning"
              lowContrast
              style={{ marginBottom: 'var(--cds-spacing-06)' }}
            />

            {/* Dashboard Metrics */}
            <DashboardMetrics
              stats={stats}
              marketingMetrics={marketingMetrics}
              systemHealth={systemHealth}
              loading={loading}
            />

            {/* Cache Status Indicator */}
            <div style={{ marginBottom: 'var(--cds-spacing-05)' }}>
              <CacheStatus userId={user?.user_id} showMetrics={true} onSyncRequest={() => fetchDashboardData()} />
            </div>

            {/* Recent Users Table */}
            <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 'var(--cds-spacing-05)',
                }}
              >
                <Heading size="md" className="cds--type-productive-heading-03">
                  Recent Signups
                </Heading>
                <Button kind="ghost" renderIcon={Restart} onClick={fetchDashboardData} disabled={loading}>
                  Refresh
                </Button>
              </div>
              <DataTable headers={tableHeaders} rows={tableRows}>
                {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                  <TableContainer>
                    <Table {...getTableProps()}>
                      <TableHead>
                        <TableRow>
                          {headers.map(header => (
                            <TableHeader {...getHeaderProps({ header })} key={header.key}>
                              {header.header}
                            </TableHeader>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map(row => (
                          <TableRow {...getRowProps({ row })} key={row.id}>
                            {row.cells.map(cell => (
                              <TableCell key={cell.id}>{cell.value}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DataTable>
            </Tile>
          </div>
        );

      case 'platform-monitoring':
        return (
          <div>
            <PlatformMonitoringDashboard />
          </div>
        );

      case 'multi-tenant':
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            <MultiTenantManagement />
          </div>
        );

      case 'analytics':
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            <InstitutionalAnalytics />
          </div>
        );

      case 'marketing':
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            <ContentManagementAdmin integrationMode="embedded" />
          </div>
        );

      case 'system':
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            <Grid narrow>
              <Column sm={4} md={4} lg={8}>
                <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
                  <Heading
                    size="md"
                    className="cds--type-productive-heading-03"
                    style={{ marginBottom: 'var(--cds-spacing-05)' }}
                  >
                    Database Management
                  </Heading>
                  <Stack gap={4}>
                    <Button
                      kind="primary"
                      renderIcon={DatabaseMessaging}
                      onClick={handleSeedTestUsers}
                      disabled={operationInProgress}
                    >
                      Seed Test Users
                    </Button>
                    <Button renderIcon={Security} onClick={handleApplyRBACPermissions} disabled={operationInProgress}>
                      Apply RBAC Permissions
                    </Button>
                    <Button renderIcon={Restart} disabled>
                      Run Database Migrations
                    </Button>
                  </Stack>
                </Tile>
              </Column>
              <Column sm={4} md={4} lg={8}>
                <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
                  <Heading
                    size="md"
                    className="cds--type-productive-heading-03"
                    style={{ marginBottom: 'var(--cds-spacing-05)' }}
                  >
                    System Configuration
                  </Heading>
                  <Stack gap={4}>
                    <Button renderIcon={Settings}>System Settings</Button>
                    <Button renderIcon={Api}>API Configuration</Button>
                    <Button renderIcon={Security}>Security Settings</Button>
                  </Stack>
                </Tile>
              </Column>
            </Grid>
          </div>
        );

      case 'ab-testing':
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            <ABTestingDashboard />
          </div>
        );

      case 'content-library':
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            <CourseLibrary />
          </div>
        );

      case 'database-management':
      case 'data-management':
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            <DataManagement />
          </div>
        );

      case 'data-routing':
        return (
          <div>
            <DataRoutingDashboard />
          </div>
        );

      case 'cache-management':
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            <RBACCacheManagement apiBaseUrl={API_BASE_URL} />
          </div>
        );

      case 'users':
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            <Stack gap={5}>
              {/* User Management Header */}
              <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--cds-spacing-03)',
                  }}
                >
                  <Heading size="md" className="cds--type-productive-heading-03">
                    Bulk Student Account Management
                  </Heading>
                  <Button kind="primary" renderIcon={Add} onClick={() => setIsCreateUserModalOpen(true)}>
                    Create Individual User
                  </Button>
                </div>
                <p
                  className="cds--type-body-01"
                  style={{
                    color: 'var(--cds-text-secondary)',
                    marginBottom: 'var(--cds-spacing-03)',
                  }}
                >
                  Manage student accounts with bulk import capabilities, COPPA compliance, and parent notifications.
                </p>
                <InlineNotification
                  title="Administrator Access"
                  subtitle="You have full user management privileges. All actions are logged for compliance."
                  kind="info"
                  lowContrast
                />
              </Tile>

              {/* Student Bulk Creation Component */}
              <div
                style={{
                  backgroundColor: 'var(--cds-layer-01)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <StudentBulkCreation />
              </div>
            </Stack>
          </div>
        );

      case 'security-settings':
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            <EnterprisePrivacy />
          </div>
        );

      default:
        return (
          <div style={{ padding: 'var(--cds-spacing-06)' }}>
            <InlineNotification
              title="Feature Under Development"
              subtitle={`The ${activeTab} section is currently being developed.`}
              kind="info"
              lowContrast
            />
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Stack gap={4}>
          <Loading withOverlay={false} />
          <p
            style={{
              color: 'var(--cds-text-secondary)',
              fontSize: 'var(--cds-body-compact-01-font-size)',
            }}
          >
            Loading admin dashboard...
          </p>
        </Stack>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          gap: 'var(--cds-spacing-05)',
          marginBottom: 'var(--cds-spacing-05)',
        }}
      >
        <AdminNavigation isOpen={isSideNavOpen} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      <div style={{ minHeight: 'calc(100vh - 200px)' }}>{renderTabContent()}</div>

      {/* Create User Modal */}
      <Modal
        open={isCreateUserModalOpen}
        onRequestClose={() => setIsCreateUserModalOpen(false)}
        modalHeading="Create New User"
        modalLabel="User Management"
        primaryButtonText="Create User"
        secondaryButtonText="Cancel"
        hasScrollingContent
      >
        <Form>
          <FormGroup legendText="User Information">
            <FluidTextInput
              id="email"
              labelText="Email Address"
              placeholder="user@example.com"
              helperText="Enter a valid email address"
              aria-describedby="email-helper"
            />
            <FluidTextInput
              id="username"
              labelText="Username"
              placeholder="username"
              helperText="Choose a unique username"
              aria-describedby="username-helper"
            />
            <FluidTextInput
              id="role"
              labelText="Role"
              placeholder="student, teacher, parent, admin"
              helperText="Select user role and permissions"
              aria-describedby="role-helper"
            />
          </FormGroup>
        </Form>
      </Modal>

      {/* Operation Progress Indicator */}
      {operationInProgress && (
        <div
          style={{
            position: 'fixed',
            top: 'var(--cds-spacing-09)',
            right: 'var(--cds-spacing-05)',
            zIndex: 9999,
          }}
        >
          <InlineNotification
            title="Operation in Progress"
            subtitle="Please wait while the operation completes..."
            kind="info"
            lowContrast
          />
        </div>
      )}
    </>
  );
};

export default AdminDashboard;
